import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useDocumentTitle } from '@leagueplatform/web-common';
import {
  ParagraphText,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { SettingsPageLayout } from 'components/settings-page-layout/settings-page-layout.component';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { useCommunicationPreferencesQuery } from 'pages/communication-preferences/use-communication-preferences-query.hook';
import { MailingAddressSection } from './mailing-address-section.component';
import { CommunicationPreferencesSection } from './communication-preferences-section.component';
import { PersonalInformationSection } from './personal-information-section.component';
import { usePersonalInfo } from './use-personal-info.hook';

export const PersonalInfoPage = () => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  usePageViewAnalytics({ screen_name: 'Personal Info' });
  useDocumentTitle(formatMessage({ id: 'STR_PERSONAL_INFO' }));

  const {
    isLoading,
    isError,
    fullName,
    preferredFirstName,
    birthDate,
    streetLine,
    cityStateLine,
  } = usePersonalInfo();

  // Contact details for the Communication Preferences card come from the
  // comm-prefs GET — never the user profile. Its initial load is folded into the
  // page's loading gate (below) so the contact rows don't flash "Not specified"
  // before the GET resolves. The error stays non-blocking: a failed load shows a
  // retrieval message on the card while the rest of the page renders, and a
  // failed *background* refetch keeps the last good data (see the !contact guard
  // on hasError).
  const {
    data: communicationPreferences,
    isLoading: isCommunicationPreferencesLoading,
    isError: isCommunicationPreferencesError,
  } = useCommunicationPreferencesQuery();
  const contact = communicationPreferences?.contact;

  const renderContent = () => {
    if (isLoading || isCommunicationPreferencesLoading) {
      return <LoadingIndicator />;
    }

    if (isError) {
      return (
        <ParagraphText role="alert" css={{ color: '$onSurfaceTextCritical' }}>
          {formatMessage({ id: 'STR_PROFILE_LOAD_ERROR' })}
        </ParagraphText>
      );
    }

    return (
      <>
        <PersonalInformationSection
          fullName={fullName}
          birthDate={birthDate ?? ''}
          preferredName={preferredFirstName ?? ''}
        />

        <MailingAddressSection
          streetLine={streetLine}
          cityStateLine={cityStateLine}
        />

        <CommunicationPreferencesSection
          mobilePhone={contact?.mobilePhone}
          preferredPhone={contact?.preferredPhone}
          email={contact?.email}
          editLinkTo={APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES}
          hasError={isCommunicationPreferencesError && !contact}
        />
      </>
    );
  };

  return (
    <SettingsPageLayout
      heading={formatMessage({ id: 'STR_PERSONAL_INFO' })}
      spacing={isBelowTablet ? '$two' : '$oneAndHalf'}
    >
      {renderContent()}
    </SettingsPageLayout>
  );
};
